import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { QuizNavigation, QuizStore } from '../../App/stores/QuizStore.js';
import { QuestionStore } from '../../Question/stores/QuestionStore.js';
import { IQuestionWizardButtons, IQuestionWizardConfiguration } from '../models';
type IButtonProps = components.base.Button.models.IButtonProps;
type IProgressIndicatorProps = components.base.ProgressIndicator.models.IProgressIndicatorProps;

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;

export class QuestionWizardStore extends StoreBase {
	static componentKey = 'questionWizard' as const;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get quizStore() {
		return this.storeContext.get<QuizStore>('quizStore');
	}

	@computed
	get questionStore() {
		return this.storeContext.get<QuestionStore>('questionStore');
	}

	@computed
	get configuration(): IQuestionWizardConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(QuestionWizardStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {
		return;
	}

	@computed
	get defaultButtons(): IQuestionWizardButtons {
		return {
			back: {
				text: 'Back',
			},
			forward: {
				text: 'Next',
			},
			finish: {
				text: 'Finish Quiz',
			},
		};
	}

	@computed
	get buttons(): IQuestionWizardButtons | undefined {
		return this.configuration && this.configuration.buttons;
	}

	@computed
	get progressIndicatorProps(): IProgressIndicatorProps {
		return {
			maxSteps: this.quizStore.totalQuestions,
			currentStep: this.quizStore.currentQuestion + 1, // Steps are 1-based
		};
	}

	@computed
	get backButtonProps(): IButtonProps {
		return {
			...((this.buttons && this.buttons.back) || this.defaultButtons.back),
			hide: this.quizStore.currentQuestion === 0,
			onClick: () => {
				this.quizStore.navigate(QuizNavigation.backward);
			},
		};
	}

	@computed
	get forwardButtonProps(): IButtonProps {
		return {
			...((this.buttons && this.buttons.forward) || this.defaultButtons.forward),
			hide: this.quizStore.currentQuestion === this.quizStore.totalQuestions - 1,
			disabled: common.commonUtils.isNullOrUndefined(this.questionStore.userAnswer),
			onClick: () => {
				this.quizStore.navigate(QuizNavigation.forward);
			},
		};
	}

	@computed
	get finishButtonProps(): IButtonProps {
		return {
			...((this.buttons && this.buttons.finish) || this.defaultButtons.finish),
			hide: this.quizStore.currentQuestion !== this.quizStore.totalQuestions - 1,
			disabled: common.commonUtils.isNullOrUndefined(this.questionStore.userAnswer),
			onClick: () => {
				this.quizStore.completeQuiz();
			},
		};
	}

	@computed
	get hasQuestions(): boolean {
		return this.quizStore.questions.length > 0;
	}
}
